"use client";

import { type ReactNode, useEffect, useRef } from "react";
import { createEvidenceLabelResolver } from "@/lib/aicheck/evidence-labeler";
import type { AggregatedProjectData, ContactTopic } from "@/types/aicheck";
import { DismissRestoreButton } from "./DismissRestoreButton";
import { MarkdownText } from "./MarkdownText";
import type { CheckSource } from "./SourceBadge";
import { SourceBadge } from "./SourceBadge";
import { StatusBadge } from "./StatusBadge";
import {
  type FeedbackContext,
  StructuredResultView,
} from "./StructuredResultView";
import { scrollElementIntoContainer } from "./scroll-utils";
import { getImportanceColor } from "./style-utils";

interface CheckItemDetailPanelProps {
  title: string;
  detail: string;
  status: string;
  importance: string;
  score?: number;
  rawAIScore?: number;
  scoreAdjusted?: boolean;
  source?: CheckSource;
  rawResult?: Record<string, unknown>;
  projectData?: AggregatedProjectData | null;
  contactTopics?: ContactTopic[] | null;
  isDismissed: boolean;
  onDismiss: () => void;
  onRestore: () => void;
  onClose: () => void;
  headerAction?: ReactNode;
  evidenceIdPrefix?: string;
  focusedEvidenceIndex?: number | null;
  highlightedEvidenceIndex?: number | null;
  onProjectDataReferenceClick?: (source: string) => void;
  onFocusedEvidenceHandled?: () => void;
  feedbackContext?: FeedbackContext;
}

export function CheckItemDetailPanel({
  title,
  detail,
  status,
  importance,
  score,
  rawAIScore,
  scoreAdjusted,
  source,
  rawResult,
  projectData,
  contactTopics,
  isDismissed,
  onDismiss,
  onRestore,
  onClose,
  headerAction,
  evidenceIdPrefix,
  focusedEvidenceIndex,
  highlightedEvidenceIndex,
  onProjectDataReferenceClick,
  onFocusedEvidenceHandled,
  feedbackContext,
}: CheckItemDetailPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusedEvidenceIndex == null || !evidenceIdPrefix) return;

    const target = document.getElementById(
      `${evidenceIdPrefix}-${focusedEvidenceIndex}`,
    );
    if (!target || !containerRef.current) {
      return;
    }

    const rafId = requestAnimationFrame(() => {
      scrollElementIntoContainer(containerRef.current, target);
      onFocusedEvidenceHandled?.();
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [evidenceIdPrefix, focusedEvidenceIndex, onFocusedEvidenceHandled]);

  const labelResolver = createEvidenceLabelResolver({
    projectData,
    contactTopics,
  });

  return (
    <div
      ref={containerRef}
      className="border border-gray-200 rounded-lg bg-gray-50/50 p-6 h-full overflow-y-auto"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <div className="flex items-center gap-3">
          {headerAction}
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
      <div className="flex items-center gap-3 mb-4">
        <StatusBadge status={status as "pass"} />
        <SourceBadge source={source} />
        <span
          className={`text-xs font-medium ${getImportanceColor(importance)}`}
        >
          {importance}
        </span>
        {score != null && (
          <span className="text-sm font-bold tabular-nums text-gray-600">
            Score: {score}
            {scoreAdjusted && rawAIScore != null && (
              <span className="text-xs font-normal text-amber-600 ml-1">
                (adjusted from {rawAIScore})
              </span>
            )}
          </span>
        )}
        <DismissRestoreButton
          isDismissed={isDismissed}
          onDismiss={onDismiss}
          onRestore={onRestore}
          variant="detail"
        />
      </div>
      <div className="text-sm text-gray-700 mb-4">
        <MarkdownText mode="block">{detail}</MarkdownText>
      </div>
      {rawResult && (
        <StructuredResultView
          result={rawResult}
          excludeKeys={["summary", "overall_score"]}
          evidenceIdPrefix={evidenceIdPrefix}
          highlightedEvidenceIndex={highlightedEvidenceIndex}
          labelResolver={labelResolver}
          onProjectDataReferenceClick={onProjectDataReferenceClick}
          feedbackContext={feedbackContext}
        />
      )}
    </div>
  );
}

export function CheckItemDetailEmpty() {
  return (
    <div className="border border-gray-200 rounded-lg bg-gray-50/50 p-6 h-full flex items-center justify-center text-sm text-gray-400">
      Select an item to see details.
    </div>
  );
}
